/**
 * Admin data-fetching hooks.
 * Each hook wraps an AdminService method with TanStack Query for caching,
 * automatic test-mode isolation, and optimistic cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useTestMode } from "../contexts/TestModeContext";
import { adminService } from "../services/admin.service";
import {
  ManagedUser,
  BanUserParams,
  UserStats,
  UserGrowthDataPoint,
  TokenPackage,
  CreateTokenPackageParams,
  SubscriptionPackage,
  CreateSubscriptionPackageParams,
  RevenueEvent,
  RevenueStats,
  RevenueChartDataPoint,
  PromoCode,
  CreatePromoCodeParams,
  MediaKit,
  MediaExample,
  UploadMediaParams,
  UpdateMediaParams,
  TokenRedemption,
  RedemptionStats,
  RedemptionFilters,
  Campaign,
  CampaignFormParams,
  LanguageEntry,
  EventTemplate,
  EventTemplateFormParams,
} from "../types";

/** Query key factory -- includes testMode to ensure cache separation between environments. */
export const adminKeys = {
  all: ["admin"] as const,
  auth: () => [...adminKeys.all, "auth"] as const,
  users: (testMode: boolean) => [...adminKeys.all, "users", testMode] as const,
  userList: (testMode: boolean, search?: string) =>
    [...adminKeys.users(testMode), "list", search] as const,
  userDetail: (testMode: boolean, id: string) =>
    [...adminKeys.users(testMode), "detail", id] as const,
  userStats: (testMode: boolean) =>
    [...adminKeys.users(testMode), "stats"] as const,
  userGrowth: (testMode: boolean, days: number) =>
    [...adminKeys.users(testMode), "growth", days] as const,
  tokenPackages: () => [...adminKeys.all, "token-packages"] as const,
  subscriptionPackages: () =>
    [...adminKeys.all, "subscription-packages"] as const,
  revenue: () => [...adminKeys.all, "revenue"] as const,
  revenueEvents: (filters?: any) =>
    [...adminKeys.revenue(), "events", filters] as const,
  revenueStats: () => [...adminKeys.revenue(), "stats"] as const,
  revenueChart: (days: number) =>
    [...adminKeys.revenue(), "chart", days] as const,
  promoCodes: () => [...adminKeys.all, "promo-codes"] as const,
  mediaKits: () => [...adminKeys.all, "media-kits"] as const,
  mediaExamples: () => [...adminKeys.all, "media-examples"] as const,
  redemptions: (testMode: boolean) =>
    [...adminKeys.all, "redemptions", testMode] as const,
  redemptionList: (testMode: boolean, filters?: RedemptionFilters) =>
    [...adminKeys.redemptions(testMode), "list", filters] as const,
  redemptionStats: (testMode: boolean) =>
    [...adminKeys.redemptions(testMode), "stats"] as const,
  campaigns: (testMode: boolean) =>
    [...adminKeys.all, "campaigns", testMode] as const,
  eventTemplates: (testMode: boolean) =>
    [...adminKeys.all, "event_templates", testMode] as const,
  languages: () => [...adminKeys.all, "languages"] as const,
};

// ============ ADMIN AUTH ============

/** Check whether the current user has admin privileges. */
export function useAdminAuth() {
  const { user } = useAuth();

  return useQuery<boolean>({
    queryKey: adminKeys.auth(),
    queryFn: async () => {
      if (!user) return false;
      return adminService.checkIsAdmin(user.uid);
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// ============ USER MANAGEMENT ============

/** Fetch the list of managed users, optionally filtered by search string. */
export function useUsers(search?: string) {
  const { testMode } = useTestMode();

  return useQuery<{ users: ManagedUser[]; hasMore: boolean }>({
    queryKey: adminKeys.userList(testMode, search),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getUsers(50, undefined, search);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/** Fetch detailed info for a single user by ID. */
export function useUserDetail(userId: string) {
  const { testMode } = useTestMode();

  return useQuery<ManagedUser | null>({
    queryKey: adminKeys.userDetail(testMode, userId),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getUserById(userId);
    },
    enabled: !!userId,
  });
}

/** Fetch aggregate user statistics (total users, new users, etc.). */
export function useUserStats() {
  const { testMode } = useTestMode();

  return useQuery<UserStats>({
    queryKey: adminKeys.userStats(testMode),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getUserStats();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/** Fetch user growth data points for charting over the given number of days. */
export function useUserGrowth(days: number = 30) {
  const { testMode } = useTestMode();

  return useQuery<UserGrowthDataPoint[]>({
    queryKey: adminKeys.userGrowth(testMode, days),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getUserGrowthData(days);
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/** Mutation to ban a user. */
export function useBanUser() {
  const queryClient = useQueryClient();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: BanUserParams) => {
      adminService.setTestMode(testMode);
      return adminService.banUser(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users(testMode) });
    },
  });
}

/** Mutation to unban a user. */
export function useUnbanUser() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (userId: string) => {
      adminService.setTestMode(testMode);
      return adminService.unbanUser(userId, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users(testMode) });
    },
  });
}

// ============ TOKEN PACKAGES ============

/** Fetch all token packages (active and inactive). */
export function useTokenPackages() {
  const { testMode } = useTestMode();

  return useQuery<TokenPackage[]>({
    queryKey: [...adminKeys.tokenPackages(), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getTokenPackages();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch only active (purchasable) token packages. */
export function useActiveTokenPackages() {
  const { testMode } = useTestMode();

  return useQuery<TokenPackage[]>({
    queryKey: [...adminKeys.tokenPackages(), "active", testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getActiveTokenPackages();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Mutation to create a new token package. */
export function useCreateTokenPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: CreateTokenPackageParams) => {
      adminService.setTestMode(testMode);
      return adminService.createTokenPackage(params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.tokenPackages() });
    },
  });
}

/** Mutation to update an existing token package. */
export function useUpdateTokenPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<TokenPackage>;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.updateTokenPackage(id, updates, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.tokenPackages() });
    },
  });
}

/** Mutation to deactivate a token package (soft-delete). */
export function useDeactivateTokenPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.deactivateTokenPackage(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.tokenPackages() });
    },
  });
}

// ============ SUBSCRIPTION PACKAGES ============

/** Fetch all subscription packages (active and inactive). */
export function useSubscriptionPackages() {
  const { testMode } = useTestMode();

  return useQuery<SubscriptionPackage[]>({
    queryKey: [...adminKeys.subscriptionPackages(), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getSubscriptionPackages();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch only active subscription packages shown to users. */
export function useActiveSubscriptionPackages() {
  const { testMode } = useTestMode();

  return useQuery<SubscriptionPackage[]>({
    queryKey: [...adminKeys.subscriptionPackages(), "active", testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getActiveSubscriptionPackages();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Mutation to create a new subscription package. */
export function useCreateSubscriptionPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: CreateSubscriptionPackageParams) => {
      adminService.setTestMode(testMode);
      return adminService.createSubscriptionPackage(params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.subscriptionPackages(),
      });
    },
  });
}

/** Mutation to update an existing subscription package. */
export function useUpdateSubscriptionPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<SubscriptionPackage>;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.updateSubscriptionPackage(id, updates, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.subscriptionPackages(),
      });
    },
  });
}

/** Mutation to deactivate a subscription package. */
export function useDeactivateSubscriptionPackage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.deactivateSubscriptionPackage(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.subscriptionPackages(),
      });
    },
  });
}

// ============ REVENUE ============

/** Fetch recent revenue events with optional type/date filters. */
export function useRevenueEvents(filters?: {
  type?: string;
  startDate?: number;
  endDate?: number;
}) {
  const { testMode } = useTestMode();

  return useQuery<RevenueEvent[]>({
    queryKey: [...adminKeys.revenueEvents(filters), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getRevenueEvents(100, filters);
    },
    staleTime: 1000 * 60 * 2,
  });
}

/** Fetch aggregate revenue statistics (total, MRR, this month, etc.). */
export function useRevenueStats() {
  const { testMode } = useTestMode();

  return useQuery<RevenueStats>({
    queryKey: [...adminKeys.revenueStats(), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getRevenueStats();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch revenue chart data points for the given number of days. */
export function useRevenueChart(days: number = 30) {
  const { testMode } = useTestMode();

  return useQuery<RevenueChartDataPoint[]>({
    queryKey: [...adminKeys.revenueChart(days), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getRevenueChartData(days);
    },
    staleTime: 1000 * 60 * 10,
  });
}

/** Fetch transactions across all users (admin-level view). */
export function useAllUserTransactions(filters?: {
  type?: string;
  startDate?: number;
  endDate?: number;
  search?: string;
}) {
  const { testMode } = useTestMode();

  return useQuery({
    queryKey: [...adminKeys.all, "all-transactions", testMode, filters],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getAllUserTransactions(100, filters);
    },
    staleTime: 1000 * 60 * 2,
  });
}

// ============ PROMO CODES ============

/** Fetch all promo codes. */
export function usePromoCodes() {
  const { testMode } = useTestMode();

  return useQuery<PromoCode[]>({
    queryKey: [...adminKeys.promoCodes(), testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getPromoCodes();
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Mutation to create a new promo code. */
export function useCreatePromoCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: CreatePromoCodeParams) => {
      adminService.setTestMode(testMode);
      return adminService.createPromoCode(params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.promoCodes() });
    },
  });
}

/** Mutation to update an existing promo code. */
export function useUpdatePromoCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<PromoCode>;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.updatePromoCode(id, updates, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.promoCodes() });
    },
  });
}

/** Mutation to deactivate a promo code. */
export function useDeactivatePromoCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.deactivatePromoCode(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.promoCodes() });
    },
  });
}

/** Mutation to re-activate a deactivated promo code. */
export function useActivatePromoCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.activatePromoCode(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.promoCodes() });
    },
  });
}

// ============ MEDIA ============

/** Fetch all media kits. */
export function useMediaKits() {
  return useQuery<MediaKit[]>({
    queryKey: adminKeys.mediaKits(),
    queryFn: () => adminService.getMediaKits(),
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch all media examples (design inspirations). */
export function useMediaExamples() {
  return useQuery<MediaExample[]>({
    queryKey: adminKeys.mediaExamples(),
    queryFn: () => adminService.getMediaExamples(),
    staleTime: 1000 * 60 * 5,
  });
}

/** Mutation to upload a new media item (kit or example). */
export function useUploadMedia() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (params: UploadMediaParams) =>
      adminService.uploadMedia(params, user!.uid),
    onSuccess: (result) => {
      if (result.type === "kit") {
        queryClient.invalidateQueries({ queryKey: adminKeys.mediaKits() });
      } else {
        queryClient.invalidateQueries({ queryKey: adminKeys.mediaExamples() });
      }
    },
  });
}

/**
 * Mutation to upload a jersey/kit with explicit front + back images and a
 * name. Always invalidates the kits cache; the legacy `useUploadMedia`
 * remains for inspirations and single-image uploads.
 */
export function useUploadMediaJersey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (params: {
      name: string;
      teamName?: string;
      frontFile: File;
      backFile: File;
    }) => adminService.uploadMediaJersey(params, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.mediaKits() });
    },
  });
}

/** Mutation to update metadata for an existing media item. */
export function useUpdateMedia() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (params: UpdateMediaParams) =>
      adminService.updateMedia(params, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.mediaKits() });
      queryClient.invalidateQueries({ queryKey: adminKeys.mediaExamples() });
    },
  });
}

/** Mutation to delete a media item and its associated storage files. */
export function useDeleteMedia() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: "kit" | "example" }) =>
      adminService.deleteMedia(id, type, user!.uid),
    onSuccess: (_, { type }) => {
      if (type === "kit") {
        queryClient.invalidateQueries({ queryKey: adminKeys.mediaKits() });
      } else {
        queryClient.invalidateQueries({ queryKey: adminKeys.mediaExamples() });
      }
    },
  });
}

// ============ TEAMS ============

/** Fetch the list of teams available in the system. */
export function useTeams() {
  const { testMode } = useTestMode();

  return useQuery<string[]>({
    queryKey: [...adminKeys.all, "teams", testMode],
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getTeams();
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/** Mutation to create a new team. */
export function useCreateTeam() {
  const queryClient = useQueryClient();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (name: string) => {
      adminService.setTestMode(testMode);
      return adminService.createTeam(name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "teams", testMode],
      });
    },
  });
}

// ============ CATEGORIES (for examples) ============

/** Fetch media example categories. */
export function useCategories() {
  return useQuery<string[]>({
    queryKey: [...adminKeys.all, "categories"],
    queryFn: () => adminService.getCategories(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/** Mutation to create a new media category. */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => adminService.createCategory(name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "categories"],
      });
    },
  });
}

/** Fetch only active media examples for the public gallery. */
export function useActiveMediaExamples() {
  return useQuery({
    queryKey: [...adminKeys.mediaExamples(), "active"],
    queryFn: () => adminService.getActiveMediaExamples(),
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch media kits designated for the homepage hero section. */
export function useHeroMediaKits() {
  return useQuery<MediaKit[]>({
    queryKey: [...adminKeys.mediaKits(), "hero"],
    queryFn: () => adminService.getHeroMediaKits(),
    staleTime: 1000 * 60 * 5,
  });
}

/** Fetch media examples designated for the homepage hero section. */
export function useHeroMediaExamples() {
  return useQuery<MediaExample[]>({
    queryKey: [...adminKeys.mediaExamples(), "hero"],
    queryFn: () => adminService.getHeroMediaExamples(),
    staleTime: 1000 * 60 * 5,
  });
}

// ============ REDEMPTIONS (Legacy) ============

/** Fetch legacy token redemptions with optional filters. */
export function useRedemptions(filters?: RedemptionFilters) {
  const { testMode } = useTestMode();

  return useQuery<{ redemptions: TokenRedemption[]; hasMore: boolean }>({
    queryKey: adminKeys.redemptionList(testMode, filters),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getRedemptions(filters);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/** Fetch aggregate redemption statistics. */
export function useRedemptionStats() {
  const { testMode } = useTestMode();

  return useQuery<RedemptionStats>({
    queryKey: adminKeys.redemptionStats(testMode),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getRedemptionStats();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/** Mutation to refund a token redemption. */
export function useRefundRedemption() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      redemptionId,
      reason,
    }: {
      redemptionId: string;
      reason: string;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.refundRedemption(redemptionId, reason, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.redemptions(testMode),
      });
    },
  });
}

// ============ TOKEN TRANSACTIONS (New System) ============

export interface TokenSpendingStats {
  totalSpent: number;
  spendingToday: number;
  spendingThisWeek: number;
  spendingThisMonth: number;
  byActionType: Record<string, { count: number; tokens: number }>;
  averageTokensPerSpend: number;
}

export interface TokenTransaction {
  id: string;
  userId: string;
  userEmail?: string;
  type: string;
  actionType?: string;
  amount: number;
  description?: string;
  status: string;
  createdAt: number;
  packageId?: string;
  pricePaid?: number;
  currency?: string;
  balanceBefore?: number;
  balanceAfter?: number;
}

/** Fetch all token transactions for admin with optional filters. */
export function useTokenTransactions(filters?: {
  type?: string;
  startDate?: number;
  endDate?: number;
  search?: string;
}): {
  data: TokenTransaction[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const { testMode } = useTestMode();

  const result = useQuery({
    queryKey: [...adminKeys.all, "token-transactions", testMode, filters],
    queryFn: async (): Promise<TokenTransaction[]> => {
      adminService.setTestMode(testMode);
      const transactions = await adminService.getAllUserTransactions(
        200,
        filters
      );
      return transactions as TokenTransaction[];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error,
  };
}

/** Fetch aggregate token spending statistics for the admin dashboard. */
export function useTokenSpendingStats(): {
  data: TokenSpendingStats | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const { testMode } = useTestMode();

  const result = useQuery({
    queryKey: [...adminKeys.all, "token-spending-stats", testMode],
    queryFn: async (): Promise<TokenSpendingStats> => {
      adminService.setTestMode(testMode);
      return adminService.getTokenSpendingStats();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error,
  };
}

// ============ LANGUAGES ============

/**
 * Fetch the list of supported languages from `localization/{code}` in RTDB.
 * Falls back to English-only when the Languages admin page hasn't shipped yet.
 */
export function useLanguages() {
  return useQuery<LanguageEntry[]>({
    queryKey: adminKeys.languages(),
    queryFn: async () => {
      const langs = await adminService.getLanguages();
      // Always ensure English is present so the form doesn't render empty
      // when the Languages page hasn't been populated yet.
      if (!langs.some((l) => l.code === "en")) {
        return [{ code: "en", name: "English", isActive: true }, ...langs];
      }
      return langs;
    },
    staleTime: 1000 * 60 * 10,
  });
}

// ============ CAMPAIGNS / COMMUNICATIONS ============

/** Fetch all communication campaigns (active and inactive). */
export function useCampaigns() {
  const { testMode } = useTestMode();

  return useQuery<Campaign[]>({
    queryKey: adminKeys.campaigns(testMode),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getCampaigns();
    },
    staleTime: 1000 * 30,
  });
}

/** Mutation to create a new campaign (uploads image then writes RTDB record). */
export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: CampaignFormParams) => {
      adminService.setTestMode(testMode);
      return adminService.createCampaign(params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.campaigns(testMode),
      });
    },
  });
}

/** Mutation to update an existing campaign. */
export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: CampaignFormParams;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.updateCampaign(id, params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.campaigns(testMode),
      });
    },
  });
}

/**
 * Trigger an immediate notification dispatch for a campaign.
 * The server returns delivered/failed counts and (for `'all'`) writes back
 * `notification.sentAtMs` so the editor's status row updates on next refetch.
 */
export function useSendCampaignNotification() {
  const queryClient = useQueryClient();
  const { testMode } = useTestMode();
  return useMutation({
    mutationFn: ({
      campaignId,
      scope,
    }: {
      campaignId: string;
      scope: "me" | "testUsers" | "all";
    }) => {
      // Notification dispatch always reads from the production campaigns
      // collection - see functions/src/campaigns/index.ts. Test mode admins
      // can still hit this; server gates by admin role.
      adminService.setTestMode(testMode);
      return adminService.sendCampaignNotification(campaignId, scope);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.campaigns(testMode),
      });
    },
  });
}

/** Mutation to delete a campaign and its image asset. */
export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.deleteCampaign(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.campaigns(testMode),
      });
    },
  });
}

// ============ EVENT TEMPLATES ============

/** Fetch all event templates (active and inactive). */
export function useEventTemplates() {
  const { testMode } = useTestMode();

  return useQuery<EventTemplate[]>({
    queryKey: adminKeys.eventTemplates(testMode),
    queryFn: () => {
      adminService.setTestMode(testMode);
      return adminService.getEventTemplates();
    },
    staleTime: 1000 * 30,
  });
}

/** Mutation to create a new event template (uploads image then writes RTDB record). */
export function useCreateEventTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (params: EventTemplateFormParams) => {
      adminService.setTestMode(testMode);
      return adminService.createEventTemplate(params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.eventTemplates(testMode),
      });
    },
  });
}

/** Mutation to update an existing event template. */
export function useUpdateEventTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: EventTemplateFormParams;
    }) => {
      adminService.setTestMode(testMode);
      return adminService.updateEventTemplate(id, params, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.eventTemplates(testMode),
      });
    },
  });
}

/** Mutation to delete an event template and its cover image asset. */
export function useDeleteEventTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { testMode } = useTestMode();

  return useMutation({
    mutationFn: (id: string) => {
      adminService.setTestMode(testMode);
      return adminService.deleteEventTemplate(id, user!.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.eventTemplates(testMode),
      });
    },
  });
}
